import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Languages } from "lucide-react";
import Logo from "@/components/Logo";

export default function OwnerLogin() {
    const { t, lang, setLang, login } = useApp();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success(t("welcome_back") + ", " + u.name);
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || t("error"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen coffee-bg flex flex-col">
            <header className="flex items-center justify-between p-5">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-home">
                    <ArrowLeft className="h-4 w-4 flip-rtl" />{t("back")}
                </Link>
                <Button variant="ghost" size="sm" onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="gap-2" data-testid="owner-lang-toggle">
                    <Languages className="h-4 w-4" />{t("switch_lang")}
                </Button>
            </header>

            <div className="flex-1 flex items-center justify-center p-6">
                <form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-border bg-card/70 backdrop-blur-sm p-8 grain space-y-6" data-testid="owner-login-form">
                    <div className="flex justify-center mb-2">
                        <Logo size={64} />
                    </div>
                    <div className="text-center">
                        <h1 className={`${lang === "ar" ? "font-ar" : "font-display-en italic"} text-3xl md:text-4xl mb-2`}>{t("owner_login")}</h1>
                        <p className="text-sm text-muted-foreground">{t("welcome_back")}</p>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="email">{t("email")}</Label>
                        <Input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="owner@cafe24.sd"
                            required
                            data-testid="owner-email-input"
                            autoComplete="email"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="password">{t("password")}</Label>
                        <Input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            data-testid="owner-password-input"
                            autoComplete="current-password"
                        />
                    </div>
                    <Button type="submit" className="w-full h-12 text-base" disabled={loading} data-testid="owner-submit-btn">
                        {loading ? t("loading") : t("login")}
                    </Button>
                    <div className="text-center pt-2">
                        <Link to="/staff-login" className="text-xs text-muted-foreground hover:text-primary" data-testid="goto-staff-login">
                            {t("staff_login")} →
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
